import { Decimal } from './decimal';

/**
 * Sort d'un ordre dénoué, tel que la situation le révèle.
 *
 * - `NON_SERVI` : rien n'a été réalisé ;
 * - `PARTIEL` : une unité de plus aurait encore tenu dans la demande — un
 *   titre pour un ordre en quantité, un titre au prix obtenu pour un ordre en
 *   montant. Le reliquat d'arrondi d'une souscription OPCVM, fractionnable,
 *   n'en est pas un ;
 * - `COMPLET` sinon.
 *
 * `null` tant que l'ordre n'est pas dénoué, ou dénoué avant que l'application
 * ne mesure ce qui a été servi.
 */
export type EtatRealisation = 'COMPLET' | 'PARTIEL' | 'NON_SERVI';

export function etatRealisation(ordre: {
  mode: string;
  quantiteDemandee: string | null;
  montantDemande: string | null;
  quantiteRealisee: string | null;
  montantRealise: string | null;
}): EtatRealisation | null {
  if (ordre.quantiteRealisee === null) return null;
  const quantite = new Decimal(ordre.quantiteRealisee);
  if (quantite.isZero()) return 'NON_SERVI';

  if (ordre.mode === 'QUANTITE') {
    if (!ordre.quantiteDemandee) return 'COMPLET';
    return quantite.lessThan(ordre.quantiteDemandee) ? 'PARTIEL' : 'COMPLET';
  }

  if (!ordre.montantDemande || ordre.montantRealise === null) return 'COMPLET';
  const realise = new Decimal(ordre.montantRealise);
  const unitaire = realise.dividedBy(quantite);
  return new Decimal(ordre.montantDemande).minus(realise).greaterThanOrEqualTo(unitaire)
    ? 'PARTIEL'
    : 'COMPLET';
}

/**
 * Un panier est complet quand tous ses ordres — hors ordres annulés — sont
 * dénoués ET servis en totalité. C'est la condition de sa clôture automatique :
 * un ordre partiel, non servi ou rejeté laisse le panier ouvert, jusqu'à ce
 * qu'une situation ultérieure en complète le reliquat ou que le back-office le
 * clôture.
 */
export function panierComplet(
  ordres: (Parameters<typeof etatRealisation>[0] & { statut: string })[],
): boolean {
  const actifs = ordres.filter((o) => o.statut !== 'ANNULE');
  return (
    actifs.length > 0 &&
    actifs.every((o) => o.statut === 'DENOUE' && etatRealisation(o) === 'COMPLET')
  );
}
